import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useCallback, useRef } from 'react'
import { queryKeys } from '@/shared/api/queryClient'
import { aiApi } from '../api'
import type {
  AIAnalysisResponse,
  RootCauseAnalysisRequest,
  FishDiagnosisRequest,
  FollowUpRequest,
  AnomalyCategory,
  AnalysisStatus,
  AnalysisType,
} from '../types'

// ==============================================================================
// Query Hooks
// ==============================================================================

/**
 * Hook to fetch list of AI analyses for a farm
 */
export function useAIAnalyses(
  farmId: string,
  params?: {
    pondId?: string
    analysisType?: AnalysisType
    anomalyCategory?: AnomalyCategory
    status?: AnalysisStatus
    startDate?: string
    endDate?: string
    cursor?: string
    limit?: number
  }
) {
  return useQuery({
    queryKey: queryKeys.ai.analyses(farmId, params),
    queryFn: () =>
      aiApi.listAnalyses({
        farmId,
        ...params,
      }),
    enabled: !!farmId,
  })
}

/**
 * Hook to fetch a single AI analysis by ID
 */
export function useAIAnalysis(analysisId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.ai.analysis(analysisId || ''),
    queryFn: () => aiApi.getAnalysis(analysisId!),
    enabled: !!analysisId,
  })
}

/**
 * Hook to check AI rate limit status
 */
export function useAIRateLimit() {
  return useQuery({
    queryKey: queryKeys.ai.rateLimit(),
    queryFn: () => aiApi.checkRateLimit(),
    staleTime: 60 * 1000, // 1 minute
  })
}

/**
 * Hook to fetch fish species reference data
 */
export function useFishSpecies(activeOnly: boolean = true) {
  return useQuery({
    queryKey: queryKeys.ai.fishSpecies(activeOnly),
    queryFn: () => aiApi.getFishSpecies(activeOnly),
    staleTime: 30 * 60 * 1000, // 30 minutes (reference data)
  })
}

// ==============================================================================
// Mutation Hooks
// ==============================================================================

/**
 * Hook to delete an AI analysis
 */
export function useDeleteAnalysis() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (analysisId: string) => aiApi.deleteAnalysis(analysisId),
    onSuccess: () => {
      // Invalidate all analyses queries
      queryClient.invalidateQueries({ queryKey: ['ai', 'analyses'] })
    },
  })
}

// ==============================================================================
// Streaming Hooks
// ==============================================================================

interface StreamingState {
  isStreaming: boolean
  content: string
  status: string
  statusHistory: string[]
  error: string | null
  result: AIAnalysisResponse | null
}

/**
 * Hook for streaming root cause analysis
 */
export function useRootCauseAnalysisStream() {
  const queryClient = useQueryClient()
  const [state, setState] = useState<StreamingState>({
    isStreaming: false,
    content: '',
    status: '',
    statusHistory: [],
    error: null,
    result: null,
  })
  const abortControllerRef = useRef<AbortController | null>(null)

  const analyze = useCallback(
    async (farmId: string, request: RootCauseAnalysisRequest) => {
      // Reset state
      setState({
        isStreaming: true,
        content: '',
        status: 'Starting analysis...',
        statusHistory: [],
        error: null,
        result: null,
      })

      // Create abort controller
      abortControllerRef.current = new AbortController()

      try {
        const stream = aiApi.analyzeRootCauseStream(farmId, request)

        for await (const chunk of stream) {
          // Check if aborted
          if (abortControllerRef.current?.signal.aborted) {
            break
          }

          switch (chunk.type) {
            case 'content':
              setState((prev) => ({
                ...prev,
                content: prev.content + (chunk.content || ''),
              }))
              break
            case 'status':
              setState((prev) => ({
                ...prev,
                status: chunk.status || prev.status,
                statusHistory: chunk.status ? [...prev.statusHistory, chunk.status] : prev.statusHistory,
              }))
              break
            case 'error':
              setState((prev) => ({
                ...prev,
                error: chunk.error || 'Unknown error',
                isStreaming: false,
              }))
              break
            case 'complete':
              setState((prev) => ({
                ...prev,
                result: chunk.final || null,
                isStreaming: false,
                status: 'Complete',
              }))
              // Invalidate queries to refresh list
              queryClient.invalidateQueries({ queryKey: ['ai', 'analyses'] })
              queryClient.invalidateQueries({ queryKey: ['ai', 'rate-limit'] })
              break
          }
        }
      } catch (error) {
        setState((prev) => ({
          ...prev,
          error: error instanceof Error ? error.message : 'Analysis failed',
          isStreaming: false,
        }))
      } finally {
        abortControllerRef.current = null
      }
    },
    [queryClient]
  )

  const abort = useCallback(() => {
    abortControllerRef.current?.abort()
    setState((prev) => ({
      ...prev,
      isStreaming: false,
      status: 'Cancelled',
    }))
  }, [])

  return {
    ...state,
    analyze,
    abort,
  }
}

/**
 * Hook for streaming fish health diagnosis
 */
export function useFishDiagnosisStream() {
  const queryClient = useQueryClient()
  const [state, setState] = useState<StreamingState>({
    isStreaming: false,
    content: '',
    status: '',
    statusHistory: [],
    error: null,
    result: null,
  })
  const abortControllerRef = useRef<AbortController | null>(null)

  const diagnose = useCallback(
    async (farmId: string, request: FishDiagnosisRequest) => {
      setState({
        isStreaming: true,
        content: '',
        status: 'Starting diagnosis...',
        statusHistory: [],
        error: null,
        result: null,
      })

      abortControllerRef.current = new AbortController()

      try {
        const stream = aiApi.diagnoseFishHealthStream(farmId, request)

        for await (const chunk of stream) {
          if (abortControllerRef.current?.signal.aborted) {
            break
          }

          switch (chunk.type) {
            case 'content':
              setState((prev) => ({
                ...prev,
                content: prev.content + (chunk.content || ''),
              }))
              break
            case 'status':
              setState((prev) => ({
                ...prev,
                status: chunk.status || prev.status,
              }))
              break
            case 'error':
              setState((prev) => ({
                ...prev,
                error: chunk.error || 'Unknown error',
                isStreaming: false,
              }))
              break
            case 'complete':
              setState((prev) => ({
                ...prev,
                result: chunk.final || null,
                isStreaming: false,
                status: 'Complete',
              }))
              queryClient.invalidateQueries({ queryKey: ['ai', 'analyses'] })
              queryClient.invalidateQueries({ queryKey: ['ai', 'rate-limit'] })
              break
          }
        }
      } catch (error) {
        setState((prev) => ({
          ...prev,
          error: error instanceof Error ? error.message : 'Diagnosis failed',
          isStreaming: false,
        }))
      } finally {
        abortControllerRef.current = null
      }
    },
    [queryClient]
  )

  const abort = useCallback(() => {
    abortControllerRef.current?.abort()
    setState((prev) => ({
      ...prev,
      isStreaming: false,
      status: 'Cancelled',
    }))
  }, [])

  return {
    ...state,
    diagnose,
    abort,
  }
}

/**
 * Hook for streaming follow-up questions
 */
export function useFollowUpStream() {
  const queryClient = useQueryClient()
  const [state, setState] = useState<StreamingState>({
    isStreaming: false,
    content: '',
    status: '',
    statusHistory: [],
    error: null,
    result: null,
  })
  const abortControllerRef = useRef<AbortController | null>(null)

  const askFollowUp = useCallback(
    async (request: FollowUpRequest) => {
      setState({
        isStreaming: true,
        content: '',
        status: 'Processing question...',
        statusHistory: [],
        error: null,
        result: null,
      })

      abortControllerRef.current = new AbortController()

      try {
        const stream = aiApi.askFollowUpStream(request)

        for await (const chunk of stream) {
          if (abortControllerRef.current?.signal.aborted) {
            break
          }

          switch (chunk.type) {
            case 'content':
              setState((prev) => ({
                ...prev,
                content: prev.content + (chunk.content || ''),
              }))
              break
            case 'status':
              setState((prev) => ({
                ...prev,
                status: chunk.status || prev.status,
              }))
              break
            case 'error':
              setState((prev) => ({
                ...prev,
                error: chunk.error || 'Unknown error',
                isStreaming: false,
              }))
              break
            case 'complete':
              setState((prev) => ({
                ...prev,
                result: chunk.final || null,
                isStreaming: false,
                status: 'Complete',
              }))
              // Refresh the analysis to get updated conversation
              queryClient.invalidateQueries({
                queryKey: queryKeys.ai.analysis(request.analysisId),
              })
              break
          }
        }
      } catch (error) {
        setState((prev) => ({
          ...prev,
          error: error instanceof Error ? error.message : 'Follow-up failed',
          isStreaming: false,
        }))
      } finally {
        abortControllerRef.current = null
      }
    },
    [queryClient]
  )

  const abort = useCallback(() => {
    abortControllerRef.current?.abort()
    setState((prev) => ({
      ...prev,
      isStreaming: false,
      status: 'Cancelled',
    }))
  }, [])

  return {
    ...state,
    askFollowUp,
    abort,
  }
}

// ==============================================================================
// Non-streaming Mutation Hooks (alternative to streaming)
// ==============================================================================

/**
 * Hook for non-streaming root cause analysis
 */
export function useRootCauseAnalysis() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      farmId,
      request,
    }: {
      farmId: string
      request: RootCauseAnalysisRequest
    }) => aiApi.analyzeRootCause(farmId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai', 'analyses'] })
      queryClient.invalidateQueries({ queryKey: ['ai', 'rate-limit'] })
    },
  })
}

/**
 * Hook for non-streaming fish diagnosis
 */
export function useFishDiagnosis() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      farmId,
      request,
    }: {
      farmId: string
      request: FishDiagnosisRequest
    }) => aiApi.diagnoseFishHealth(farmId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai', 'analyses'] })
      queryClient.invalidateQueries({ queryKey: ['ai', 'rate-limit'] })
    },
  })
}

/**
 * Hook for non-streaming follow-up
 */
export function useFollowUp() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: FollowUpRequest) => aiApi.askFollowUp(request),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.ai.analysis(variables.analysisId),
      })
    },
  })
}
